import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, BookOpen } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';
import OptimizedImage from '../ui/OptimizedImage';

// Placeholder articles for when database is empty
const PLACEHOLDER_ARTICLES = [
  {
    id: 'placeholder-1',
    title: 'Top 5 Reasons to Choose a Chauffeur for Brussels Airport',
    slug: 'chauffeur-brussels-airport',
    excerpt: 'Discover why discerning travelers prefer professional chauffeur services over taxis and rideshares for airport transfers.',
    image_url: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=800&q=80',
    published_at: new Date().toISOString(),
  },
  {
    id: 'placeholder-2',
    title: 'Corporate Travel Etiquette: Making the Right Impression',
    slug: 'corporate-travel-etiquette',
    excerpt: 'Learn how professional transportation enhances your business image and helps you arrive prepared for important meetings.',
    image_url: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=800&q=80',
    published_at: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    id: 'placeholder-3',
    title: 'Hidden Gems: Day Trips from Brussels with a Private Chauffeur',
    slug: 'day-trips-brussels',
    excerpt: 'Explore the most beautiful destinations around Belgium in comfort and style with our curated day trip experiences.',
    image_url: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?auto=format&fit=crop&w=800&q=80',
    published_at: new Date(Date.now() - 172800000).toISOString(),
  },
];

const FeaturedBlogSection = () => {
  const { data: posts, isLoading } = useQuery({
    queryKey: ['featured-blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false })
        .limit(3);
      
      if (error) {
        console.error('Error fetching featured blog posts:', error);
        throw error;
      }
      return data;
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });

  const displayPosts = posts?.length ? posts : PLACEHOLDER_ARTICLES;
  const isPlaceholder = !posts?.length;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <section className="py-20 bg-neutral-cream" aria-labelledby="blog-heading">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-primary uppercase tracking-widest text-sm font-medium">Our Blog</span>
          <h2 id="blog-heading" className="text-3xl md:text-4xl font-serif font-bold text-secondary mt-3 mb-4">
            Travel Insights & Tips
          </h2>
          <div className="w-24 h-1 bg-primary mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg">
            Stay updated with our latest news, travel guides, and luxury insights
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayPosts.map((post, index) => (
              <article
                key={post.id}
                className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative h-52 overflow-hidden">
                  <OptimizedImage
                    src={post.image_url || 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&w=800&q=80'}
                    alt={post.title}
                    className="w-full h-full group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent pointer-events-none"></div>
                  {index === 0 && !isPlaceholder && (
                    <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1 rounded-full text-xs font-medium">
                      Latest
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.published_at || new Date().toISOString())}
                    </span>
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      5 min read
                    </span>
                  </div>
                  <h3 className="text-xl font-serif font-bold text-secondary mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                  {isPlaceholder ? (
                    <span className="inline-flex items-center text-primary font-medium text-sm">
                      Coming soon <ArrowRight className="w-4 h-4 ml-2" />
                    </span>
                  ) : (
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-primary font-medium text-sm hover:gap-2 transition-all"
                    >
                      Read article <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="inline-flex items-center px-8 py-4 bg-secondary text-white font-medium rounded-lg hover:bg-secondary-dark transition-colors"
          >
            View All Articles <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedBlogSection;
